/**
 * usePrefersReducedMotion
 *
 * SHS Visual Experience System — Sprint 29.1A.
 *
 * Reads the OS/browser accessibility preference:
 *   window.matchMedia('(prefers-reduced-motion: reduce)')
 *
 * Design notes:
 *   - SSR-safe: returns `false` if `window` or `matchMedia` are unavailable.
 *     `false` is the safest server-side default because the client will
 *     re-evaluate on mount; if we returned `true` server-side, animation
 *     would flash on hydration for users who had NOT requested reduced
 *     motion, which is a worse UX than the reverse.
 *   - Uses `addEventListener('change', ...)` when supported, falling back
 *     to the legacy `addListener` for older Safari builds so we do not
 *     silently drop preference changes.
 *   - Cleans up listeners on unmount — no memory leaks.
 *   - No dependencies. Pure React + browser API.
 *
 * IMPORTANT (Sprint 29.1A):
 *   This hook is the accessibility gate for the future Living Sleep System.
 *   Reduced motion always wins. Do not add options that override the user's
 *   OS preference.
 */

import { useEffect, useState } from 'react';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Returns `true` when the user has requested reduced motion at the OS or
 * browser level; `false` otherwise. Updates in real time if the preference
 * changes during the session.
 */
export function usePrefersReducedMotion() {
  const [prefersReduced, setPrefersReduced] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    try {
      return window.matchMedia(REDUCED_MOTION_QUERY).matches;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    let mql;
    try {
      mql = window.matchMedia(REDUCED_MOTION_QUERY);
    } catch {
      return undefined;
    }

    // Keep state in sync with the current value at subscription time.
    setPrefersReduced(mql.matches);

    const handleChange = (event) => {
      setPrefersReduced(event.matches);
    };

    // Modern browsers.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handleChange);
      return () => mql.removeEventListener('change', handleChange);
    }

    // Legacy Safari (< 14) and old Edge fallback.
    if (typeof mql.addListener === 'function') {
      mql.addListener(handleChange);
      return () => mql.removeListener(handleChange);
    }

    return undefined;
  }, []);

  return prefersReduced;
}

export default usePrefersReducedMotion;
